/**
 * Employee detail-update requests — the change an employee asks HR to make to
 * a section of their own record (education, documents, family, bank, …).
 *
 * The contract is deliberately section-agnostic: a request carries a list of
 * changed fields and/or a list of attachments, so a new section needs no new
 * type and no new renderer.
 */

export enum DetailUpdateRequestStatus {
  Pending = 'pending',
  Approved = 'approved',
  Rejected = 'rejected',
  Withdrawn = 'withdrawn',
  /** Saved at once under the company's change rule, no review. */
  Applied = 'applied',
}

/** Which side of the change an attachment belongs to. */
export enum DetailUpdateAttachmentSide {
  /** The file currently held on the employee's record. */
  Current = 'current',
  /** The file the employee is asking to put in its place. */
  Requested = 'requested',
}

/**
 * One changed field on a request.
 *
 * `from`/`to` are already display-formatted by whoever builds the request —
 * a date, a masked document number, a currency amount. `null` means the value
 * does not exist on that side, which is what distinguishes the three shapes a
 * change can take:
 * - edit: both set
 * - addition: `from` is null
 * - removal: `to` is null
 */
export interface DetailUpdateRequestField {
  id: string;
  /** Already-translated field name, e.g. "Phone number". */
  label: string;
  from?: string | null;
  to?: string | null;
  /**
   * A value only the side-by-side comparison may show whole, such as a bank
   * account number (D57). The request card, which is a list, masks it to its
   * last 3 characters.
   */
  sensitive?: boolean;
}

export interface DetailUpdateRequestAttachment {
  id: string;
  side: DetailUpdateAttachmentSide;
  fileName: string;
  /** Short type label, e.g. "PDF". */
  fileType?: string;
  /** Display-ready size, e.g. "240 KB" — apps format it with their own `formatFileSize`. */
  fileSize?: string;
  /** Opens the full preview; the tile is inert without it. */
  url?: string;
  thumbnailUrl?: string | null;
  mimeType?: string | null;
}

export interface DetailUpdateRequest {
  id: string;
  /** Section the request belongs to, e.g. "education" — used to group/filter. */
  section?: string;
  subject?: string;
  /** Already-translated section name, e.g. "Family Details". */
  sectionLabel?: string;
  status: DetailUpdateRequestStatus;
  fields?: DetailUpdateRequestField[];
  attachments?: DetailUpdateRequestAttachment[];
  /** The employee's justification for the change. */
  reason?: string;
  /** The reviewer's note, shown once the request is decided. */
  decisionNote?: string;
  /**
   * Display-ready line for a two-step request the manager already approved
   * (HR approves next), e.g. "Approved by Paul Gagnon (manager) on Oct 21".
   */
  managerApprovalNote?: string;
  /** Display-ready submission date, e.g. "Sep 4, 2026". */
  submittedAt?: string;
}
